import React, { useState, useMemo } from 'react';
import { MoreHorizontal, Plus, ArrowUpDown, Filter, Download, Search } from "lucide-react";
import { Badge } from '@/components/ui/badge';
import Button from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Input from '@/components/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import Select from '@/components/ui/Select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAdminTheme } from '../../contexts/AdminThemeContext';

// Sample data - replace with your actual data
const sampleData = [
  {
    id: 1,
    header: "Cover page",
    type: "Cover page",
    status: "In Process",
    target: "18",
    limit: "5",
    reviewer: "Eddie Lake",
    lastModified: "2024-01-15",
    priority: "High"
  },
  {
    id: 2,
    header: "Table of contents", 
    type: "Table of contents",
    status: "Done",
    target: "29",
    limit: "24", 
    reviewer: "Eddie Lake",
    lastModified: "2024-01-14",
    priority: "Medium"
  },
  {
    id: 3,
    header: "Executive summary",
    type: "Narrative",
    status: "Done", 
    target: "10",
    limit: "13",
    reviewer: "Eddie Lake",
    lastModified: "2024-01-13",
    priority: "High"
  },
  {
    id: 4,
    header: "Technical approach",
    type: "Narrative",
    status: "Done",
    target: "27", 
    limit: "23",
    reviewer: "Jamik Tashpulatov",
    lastModified: "2024-01-12",
    priority: "Medium"
  },
  {
    id: 5,
    header: "User Management System",
    type: "Technical content",
    status: "In Process",
    target: "15",
    limit: "20",
    reviewer: "Sarah Chen",
    lastModified: "2024-01-11",
    priority: "High"
  },
  {
    id: 6,
    header: "Analytics Dashboard", 
    type: "Technical content",
    status: "Done",
    target: "25",
    limit: "30",
    reviewer: "Mike Johnson",
    lastModified: "2024-01-10",
    priority: "Low"
  }
];

export function EnhancedDataTable() {
  const { currentColors } = useAdminTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortField, setSortField] = useState("lastModified");
  const [sortDirection, setSortDirection] = useState("desc");
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Filter and sort data
  const filteredData = useMemo(() => {
    let filtered = sampleData.filter(item => {
      const matchesSearch = item.header.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.reviewer.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === "all" || item.status === statusFilter;
      const matchesType = typeFilter === "all" || item.type === typeFilter;
      
      return matchesSearch && matchesStatus && matchesType;
    });

    // Sort data
    filtered.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (sortDirection === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [searchTerm, statusFilter, typeFilter, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleRowSelect = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const getStatusBadge = (status) => {
    const variants = {
      "Done": { 
        style: { backgroundColor: `${currentColors.success}20`, color: currentColors.success },
        text: "✓ Done"
      },
      "In Process": { 
        style: { backgroundColor: `${currentColors.warning}20`, color: currentColors.warning },
        text: "⏳ In Process"
      },
      "Not Started": { 
        style: { backgroundColor: `${currentColors.error}20`, color: currentColors.error },
        text: "○ Not Started"
      }
    };
    
    const variant = variants[status] || variants["Not Started"];
    
    return (
      <Badge variant="outline" style={variant.style} className="border-0 text-xs font-medium">
        {variant.text}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      "High": { backgroundColor: `${currentColors.error}15`, color: currentColors.error },
      "Medium": { backgroundColor: `${currentColors.warning}15`, color: currentColors.warning },
      "Low": { backgroundColor: `${currentColors.success}15`, color: currentColors.success }
    };
    
    const variant = variants[priority] || variants["Low"];
    
    return (
      <Badge variant="outline" style={variant} className="border-0 text-xs">
        {priority}
      </Badge>
    );
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Project Management</CardTitle>
              <CardDescription>
                Manage your project components and track progress
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              <Button size="sm" style={{ backgroundColor: currentColors.primary, color: 'white' }}>
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          <Tabs defaultValue="all" className="space-y-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <TabsList>
                <TabsTrigger value="all">All Items</TabsTrigger>
                <TabsTrigger value="in-progress">In Progress</TabsTrigger>
                <TabsTrigger value="done">Completed</TabsTrigger>
              </TabsList>
              
              <div className="flex gap-2">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4" style={{ color: currentColors.textMuted }} />
                  <Input
                    placeholder="Search items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8 w-64"
                  />
                </div>
                
                {/* Status Filter */}
                <Select 
                  value={statusFilter} 
                  onChange={setStatusFilter}
                  options={[
                    { value: "all", label: "All Status" },
                    { value: "Done", label: "Done" },
                    { value: "In Process", label: "In Process" },
                    { value: "Not Started", label: "Not Started" }
                  ]}
                  className="w-32"
                  placeholder="Status"
                />
                
                {/* Type Filter */}
                <Select 
                  value={typeFilter} 
                  onChange={setTypeFilter}
                  options={[
                    { value: "all", label: "All Types" },
                    { value: "Narrative", label: "Narrative" },
                    { value: "Technical content", label: "Technical" },
                    { value: "Cover page", label: "Cover page" },
                    { value: "Table of contents", label: "Table of contents" }
                  ]}
                  className="w-40"
                  placeholder="Type"
                />
              </div>
            </div>
            
            <TabsContent value="all" className="space-y-4">
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow style={{ backgroundColor: currentColors.surface }}>
                      <TableHead className="w-12">
                        <input
                          type="checkbox"
                          checked={selectedRows.size === filteredData.length && filteredData.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRows(new Set(filteredData.map(item => item.id)));
                            } else {
                              setSelectedRows(new Set());
                            }
                          }}
                          className="rounded"
                        />
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50" 
                        onClick={() => handleSort('header')}
                      >
                        <div className="flex items-center gap-2">
                          Header
                          <ArrowUpDown className="h-4 w-4" />
                        </div>
                      </TableHead>
                      <TableHead>Section Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead className="text-right">Target</TableHead>
                      <TableHead className="text-right">Limit</TableHead>
                      <TableHead>Reviewer</TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort('lastModified')}
                      >
                        <div className="flex items-center gap-2">
                          Last Modified
                          <ArrowUpDown className="h-4 w-4" />
                        </div>
                      </TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.map((item) => (
                      <TableRow 
                        key={item.id}
                        className={selectedRows.has(item.id) ? 'bg-muted/50' : ''}
                      >
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedRows.has(item.id)}
                            onChange={() => handleRowSelect(item.id)}
                            className="rounded"
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {item.header}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {item.type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(item.status)}
                        </TableCell>
                        <TableCell>
                          {getPriorityBadge(item.priority)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {item.target}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {item.limit}
                        </TableCell>
                        <TableCell>{item.reviewer}</TableCell>
                        <TableCell className="text-sm" style={{ color: currentColors.textMuted }}>
                          {new Date(item.lastModified).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem>Edit</DropdownMenuItem>
                              <DropdownMenuItem>Duplicate</DropdownMenuItem>
                              <DropdownMenuItem>View Details</DropdownMenuItem>
                              <DropdownMenuItem className="text-red-600">Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Footer with pagination info */}
              <div className="flex items-center justify-between text-sm" style={{ color: currentColors.textMuted }}>
                <div>
                  {selectedRows.size > 0 && (
                    <span>{selectedRows.size} of {filteredData.length} row(s) selected</span>
                  )}
                </div>
                <div>
                  Showing {filteredData.length} of {sampleData.length} items
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="in-progress">
              <div className="text-center py-8" style={{ color: currentColors.textMuted }}>
                Showing items in progress...
              </div>
            </TabsContent>
            
            <TabsContent value="done">
              <div className="text-center py-8" style={{ color: currentColors.textMuted }}>
                Showing completed items...
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
